import DeleteOutline from '@mui/icons-material/DeleteOutline';
import { Alert, Autocomplete, Chip, Grid, IconButton, Tooltip, Typography } from '@mui/material';
import TextField from '@mui/material/TextField';
import PropTypes from 'prop-types';
import { useEffect, useMemo } from 'react';
import { useSelector } from '../../../../redux/store';

AddMoreCategoryAutocomplete.propTypes = {
  value: PropTypes.object,
  setValue: PropTypes.func,
  setError: PropTypes.func,
};
export default function AddMoreCategoryAutocomplete({ value, setValue, setError }) {
  const { invoicingGLCategories, invoicingGLs } = useSelector((state) => state.invoicing.data);


  const catsWithSubcat = [...new Set(invoicingGLs.map(i => i.categoryId))];

  const options = useMemo(() => invoicingGLCategories?.filter(c => c.facilityId === value?.facility?.value && !value.deleteCats?.find(d => d.category === c.category))
    ?.map(cat => ({
      category: cat.category, categoryDesc: cat.description, facilityId: cat.facilityId, categoryId: cat.categoryId,
      hasSubCategory: catsWithSubcat.includes(cat.categoryId)
    }))
    , [invoicingGLCategories, value.facility, value?.deleteCats])

  const categoryExistsError = useMemo(() => options?.filter(o => o.category?.toUpperCase() === value?.category?.toUpperCase()).length > 0
    , [value.category, options]);

  const categoryBlankError = useMemo(() => !value.category, [value.category]);

  useEffect(() => {
    setError(categoryBlankError);
  }, [categoryBlankError, setError])


  return (
    <Grid container spacing={2}>
      <Grid item xs={6}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          GL Department Number
        </Typography>
        <Autocomplete
          autoSelect
          freeSolo
          size="small"
          value={value}
          onChange={(_, newValue) => {
            if (newValue?.category) {
              setValue((v) => ({
                ...v, category: newValue.category,
                categoryId: newValue.categoryId,
                categoryDesc: newValue.categoryDesc
              }))
            }
            else if (typeof newValue === 'string') {
              const catId = options?.find(o => o.category?.toUpperCase() === newValue?.toUpperCase())?.categoryId;
              setValue((v) => ({ ...v, category: newValue, categoryId: catId ?? 0 }))
            }
            else if (!newValue) setValue((v) => ({ ...v, category: "", categoryId: 0 }))
          }}
          options={options}
          getOptionLabel={(option) => (option?.category ?? option)}
          renderOption={(props, option) => {
            const { key, ...optionProps } = props;
            return (
              <Grid container alignItems='center' justifyContent='space-between' key={key} {...optionProps}>
                <Grid item xs>
                  {option.category}
                  {option.categoryDesc && (
                    <Typography variant="caption">
                      {` - ${option.categoryDesc}`}
                    </Typography>
                  )}
                </Grid>
                <Grid item >
                  <Tooltip title={option.hasSubCategory ? "item has associated gl codes" : option.category === value.category ?
                    "item is selected" : ""}>
                    <span>
                      <IconButton disabled={option.category === value.category || option.hasSubCategory} color="error" size='small'
                        onClick={(e) => {
                          e.stopPropagation();
                          if (value.category !== option.category) {
                            const deleteCategories = value.deleteCats ?? [];
                            setValue(v => ({ ...v, deleteCats: [...deleteCategories, option] }))
                          }
                        }}
                      >
                        <DeleteOutline fontSize='small' />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Grid>
              </Grid>
            );
          }}
          renderInput={(params) => <TextField {...params} autoComplete="off"/>}
          sx={{ width: "100%", flexGrow: 1 }}
        />
      </Grid>
      <Grid item xs={6}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          Name
        </Typography>
        <Autocomplete
          size="small"
          freeSolo
          autoSelect
          value={value.categoryDesc}
          onChange={(_, newValue) => {
            if (newValue?.categoryDesc) {
              setValue((v) => ({
                ...v,
                category: newValue.category,
                categoryId: newValue.categoryId,
                categoryDesc: newValue.categoryDesc
              }))
            }
            else if (typeof newValue === 'string') setValue((v) => ({ ...v, categoryDesc: newValue }))
            else if (!newValue) setValue((v) => ({ ...v, categoryDesc: "" }))
          }}
          options={options}
          renderInput={(params) => <TextField {...params} autoComplete="off"/>}
          getOptionLabel={(option) => (option?.categoryDesc ?? option)}
          renderOption={(props, option) => {
            const { key, ...optionProps } = props;
            return (
              <li key={key} {...optionProps}>
                {option.category} {option.categoryDesc && <Typography variant="caption">{` - ${option.categoryDesc}`}
                </Typography>}
              </li>
            );
          }}
        />
      </Grid>

      {categoryExistsError &&
        <Alert size="small" severity="warning" sx={{ width: '100%', ml: 2, mt: 1 }}>
          Existing GL department will be updated
        </Alert>
      }
      {categoryBlankError &&
        <Alert size="small" severity="error" sx={{ width: '100%', ml: 2, mt: 1 }}>
          Missing GL department
        </Alert>
      }
      {value.deleteCats?.length > 0 &&
        <Alert size="small" severity="error" sx={{ width: '100%', ml: 2, mt: 1 }} icon={<DeleteOutline />} variant="outlined">
          <Typography sx={{ mb: 1 }}> GL Departments to Delete:</Typography>
          {value.deleteCats?.map(c => <Chip key={c.category} label={`${c.category} - ${c.categoryDesc}`} sx={{ mr: 1 }}
            onDelete={() => {
              const deletecats = value?.deleteCats;
              setValue(v => ({ ...v, deleteCats: deletecats.filter(d => d.category !== c.category) }))
            }
            } />)}
        </Alert>
      }
    </Grid>

  );
}